import React, { useState, useRef, useEffect } from 'react';
import { 
  Image, Video, Upload, ArrowUp, ArrowDown, ArrowLeft,
  X, Loader2, Link as LinkIcon, Clock, Trash2, Plus, Shuffle, MousePointerClick, Monitor, Eye
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import BackgroundClicksChart from '../components/BackgroundClicksChart';
import BackgroundPreviewSlideshow from '../components/BackgroundPreviewSlideshow';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBgLinkInput = ({ bgId, initialLink, onSave }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current && inputRef.current !== document.activeElement) {
      inputRef.current.value = initialLink || '';
    }
  }, [initialLink]);

  return (
    <input
      ref={inputRef}
      type="text"
      defaultValue={initialLink || ''}
      onBlur={(e) => {
        const val = e.target.value;
        if (val !== (initialLink || '')) onSave(bgId, 'link', val);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      placeholder="https://..."
      data-testid={`admin-bg-link-input-${bgId}`}
      className="w-full bg-slate-800/50 border border-blue-500/20 rounded-lg px-3 py-1.5 text-sm text-white mt-1"
    />
  );
};

const AdminBackgrounds = () => {
  const { user, token, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [backgrounds, setBackgrounds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newDuration, setNewDuration] = useState(10);
  const [newLink, setNewLink] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState({});
  const [loadingBgs, setLoadingBgs] = useState(true);
  const [randomOrder, setRandomOrder] = useState(false);
  const [savingRandomOrder, setSavingRandomOrder] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate('/');
    if (!loading && user && !user.is_admin) navigate('/profile');
  }, [loading, isAuthenticated, user]);

  useEffect(() => {
    if (user?.is_admin && token) loadBackgrounds();
  }, [user, token]);

  const loadBackgrounds = async () => {
    try {
      const res = await axios.get(`${API}/admin/default-backgrounds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackgrounds(res.data.backgrounds || []);
      setRandomOrder(res.data.random_order || false);
    } catch (error) {
      console.error('Error loading backgrounds:', error);
    } finally {
      setLoadingBgs(false);
    }
  };

  const toggleRandomOrder = async () => {
    setSavingRandomOrder(true);
    try {
      const newValue = !randomOrder;
      await axios.put(`${API}/admin/default-backgrounds-settings`, {
        random_order: newValue
      }, { headers: { Authorization: `Bearer ${token}` } });
      setRandomOrder(newValue);
      toast({ title: newValue ? 'Ordre aléatoire activé' : 'Ordre séquentiel activé' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de modifier le paramètre', variant: 'destructive' });
    } finally {
      setSavingRandomOrder(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    if (file.type.startsWith('video/')) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviewUrl(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadBackground = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const CHUNK_SIZE = 5 * 1024 * 1024;
      const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);
      const initRes = await axios.post(`${API}/admin/default-backgrounds/initiate`, {
        filename: selectedFile.name, file_size: selectedFile.size,
        mime_type: selectedFile.type, total_chunks: totalChunks
      }, { headers: { Authorization: `Bearer ${token}` } });
      const uploadId = initRes.data.upload_id;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const chunk = selectedFile.slice(start, start + CHUNK_SIZE);
        const reader = new FileReader();
        const base64 = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(chunk);
        });
        await axios.post(`${API}/user/background/chunk`, {
          upload_id: uploadId, chunk_index: i, chunk_data: base64
        });
      }

      await axios.post(`${API}/admin/default-backgrounds/complete`, {
        upload_id: uploadId, duration: newDuration, link: newLink
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast({ title: 'Fond ajouté avec succès' });
      setSelectedFile(null);
      setPreviewUrl(null);
      setNewDuration(10);
      setNewLink('');
      loadBackgrounds();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Échec de l\'upload', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const updateBackground = async (bgId, field, value) => {
    setSaving(prev => ({ ...prev, [bgId]: true }));
    try {
      await axios.put(`${API}/admin/default-backgrounds/${bgId}`, {
        [field]: value
      }, { headers: { Authorization: `Bearer ${token}` } });
      setBackgrounds(prev => prev.map(bg => bg.id === bgId ? { ...bg, [field]: value } : bg));
      toast({ title: 'Fond mis à jour' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(prev => ({ ...prev, [bgId]: false }));
    }
  };

  const deleteBackground = async (bgId) => {
    if (!confirm('Supprimer ce fond ?')) return;
    try {
      await axios.delete(`${API}/admin/default-backgrounds/${bgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackgrounds(prev => prev.filter(bg => bg.id !== bgId));
      toast({ title: 'Fond supprimé' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const moveBackground = async (index, direction) => {
    const newBgs = [...backgrounds];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newBgs.length) return;
    [newBgs[index], newBgs[swapIndex]] = [newBgs[swapIndex], newBgs[index]];
    newBgs.forEach((bg, i) => bg.order = i);
    setBackgrounds(newBgs);
    for (const bg of newBgs) {
      await axios.put(`${API}/admin/default-backgrounds/${bg.id}`, {
        order: bg.order
      }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  };

  if (loading || !user?.is_admin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 to-blue-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-blue-950 text-white">
      <Toaster />
      <NavigationMenu />
      <main className="max-w-6xl mx-auto px-6 pt-28 pb-20">
        <Link to="/admin/dashboard?tab=settings" className="inline-flex items-center gap-2 text-blue-300/60 hover:text-blue-300 mb-8 transition-colors" data-testid="back-to-settings">
          <ArrowLeft className="w-4 h-4" /> Retour à la configuration
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-blue-500/20 rounded-xl">
            <Monitor className="w-8 h-8 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Fonds d'écran par défaut</h1>
            <p className="text-blue-300/60">Fonds affichés pour les utilisateurs non-Pro</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Upload section */}
          <div className="bg-slate-900/60 border border-blue-500/20 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-400" /> Ajouter un fond
            </h2>
            <div className="flex gap-6 items-start">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-48 h-28 border-2 border-dashed border-blue-500/30 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-400/50 hover:bg-blue-500/5 transition-all flex-shrink-0 overflow-hidden"
              >
                {previewUrl ? (
                  selectedFile?.type?.startsWith('video/') ? (
                    <video src={previewUrl} className="w-full h-full object-cover" muted />
                  ) : (
                    <img src={previewUrl} className="w-full h-full object-cover" alt="Preview" />
                  )
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-blue-300/40 mb-1" />
                    <span className="text-xs text-blue-300/40">Image ou vidéo</span>
                  </>
                )}
                <input ref={fileInputRef} type="file" accept="image/*,video/*" onChange={handleFileSelect} className="hidden" />
              </div>
              <div className="flex-1 space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-blue-300/60 flex items-center gap-1"><Clock className="w-3 h-3" /> Durée (sec)</label>
                    <input type="number" value={newDuration} onChange={e => setNewDuration(parseInt(e.target.value) || 10)} min={3} max={120}
                      className="w-full bg-slate-800/50 border border-blue-500/20 rounded-lg px-3 py-1.5 text-sm text-white mt-1" />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-blue-300/60 flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Lien cliquable</label>
                    <input type="url" value={newLink} onChange={e => setNewLink(e.target.value)} placeholder="https://..."
                      className="w-full bg-slate-800/50 border border-blue-500/20 rounded-lg px-3 py-1.5 text-sm text-white mt-1" />
                  </div>
                </div>
                <Button onClick={uploadBackground} disabled={!selectedFile || uploading}
                  className="bg-blue-600 hover:bg-blue-500 text-white">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Upload...</> : <><Upload className="w-4 h-4 mr-2" /> Ajouter</>}
                </Button>
              </div>
            </div>
          </div>

          {/* Backgrounds list */}
          <div className="bg-slate-900/60 border border-blue-500/20 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Monitor className="w-5 h-5 text-blue-400" /> Séquence des fonds ({backgrounds.length})
              </h2>
              <button
                onClick={toggleRandomOrder}
                disabled={savingRandomOrder}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  randomOrder
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                    : 'bg-slate-700/50 text-blue-300/50 border border-blue-500/10 hover:border-blue-500/30'
                }`}
                data-testid="random-order-toggle"
              >
                {savingRandomOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shuffle className="w-4 h-4" />}
                Ordre aléatoire
              </button>
            </div>
            <p className="text-blue-300/50 text-sm">
              {randomOrder
                ? 'Les fonds seront mélangés aléatoirement à chaque visite.'
                : 'Les fonds défilent automatiquement pour les visiteurs non-Pro.'}
            </p>

            {/* Stats summary */}
            <div className="flex items-center gap-4 bg-slate-800/30 border border-blue-500/10 rounded-xl px-4 py-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="text-sm text-white font-medium" data-testid="admin-bg-views-total">{backgrounds.reduce((sum, bg) => sum + (bg.views || 0), 0)} affichages</p>
                  <p className="text-xs text-blue-300/50">sur {backgrounds.length} fond(s)</p>
                </div>
              </div>
              {backgrounds.some(bg => (bg.clicks || 0) > 0) && (
                <div className="flex items-center gap-2 border-l border-blue-500/20 pl-4">
                  <MousePointerClick className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-sm text-white font-medium" data-testid="admin-bg-clicks-total">{backgrounds.reduce((sum, bg) => sum + (bg.clicks || 0), 0)} clics</p>
                    <p className="text-xs text-blue-300/50">sur {backgrounds.filter(bg => bg.link).length} fond(s) avec lien</p>
                  </div>
                </div>
              )}
            </div>

            {/* Click Stats Chart */}
            <BackgroundClicksChart userId="default" token={token} backgrounds={backgrounds} isAdmin={true} />
            {/* Cross-fade Preview */}
            {backgrounds.length >= 2 && (
              <BackgroundPreviewSlideshow backgrounds={backgrounds} />
            )}

            {loadingBgs ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
              </div>
            ) : backgrounds.length === 0 ? (
              <div className="text-center py-12 text-blue-300/40">
                <Image className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun fond par défaut configuré</p>
                <p className="text-xs mt-1">Ajoutez des fonds ci-dessus pour les utilisateurs non-Pro</p>
              </div>
            ) : (
              backgrounds.map((bg, i) => (
                <div key={bg.id || i} className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-4 flex gap-4" data-testid={`admin-bg-item-${i}`}>
                  <div className="w-48 h-28 rounded-lg overflow-hidden flex-shrink-0 bg-slate-900 relative">
                    {bg.type === 'video' ? (
                      <video src={bg.url?.startsWith('http') ? bg.url : `${BACKEND_URL}${bg.url}`} className="w-full h-full object-cover" controls playsInline />
                    ) : (
                      <img src={bg.url?.startsWith('http') ? bg.url : `${BACKEND_URL}${bg.url}`} className="w-full h-full object-cover" alt={`Fond ${i + 1}`} />
                    )}
                    <div className="absolute top-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                      {bg.type === 'video' ? <Video className="w-3 h-3 inline mr-1" /> : <Image className="w-3 h-3 inline mr-1" />}
                      #{i + 1}
                    </div>
                    <div className="absolute bottom-1 right-1 flex gap-1">
                      <div className="bg-black/60 text-cyan-300 text-xs px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`admin-bg-views-${i}`}>
                        <Eye className="w-3 h-3" />
                        {bg.views || 0}
                      </div>
                      {bg.link && (
                        <div className="bg-black/60 text-blue-300 text-xs px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`admin-bg-clicks-${i}`}>
                          <MousePointerClick className="w-3 h-3" />
                          {bg.clicks || 0}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="text-xs text-blue-300/60 flex items-center gap-1"><Clock className="w-3 h-3" /> Durée (sec)</label>
                        <input type="number" value={bg.duration || 10}
                          onChange={e => updateBackground(bg.id, 'duration', parseInt(e.target.value) || 10)} min={3} max={120}
                          className="w-full bg-slate-800/50 border border-blue-500/20 rounded-lg px-3 py-1.5 text-sm text-white mt-1" />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-blue-300/60 flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Lien cliquable</label>
                        <AdminBgLinkInput bgId={bg.id} initialLink={bg.link} onSave={updateBackground} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => moveBackground(i, 'up')} disabled={i === 0}
                        className="p-1.5 rounded-lg bg-slate-700/50 text-blue-300/60 hover:text-white hover:bg-slate-600/50 disabled:opacity-30 transition-all">
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveBackground(i, 'down')} disabled={i === backgrounds.length - 1}
                        className="p-1.5 rounded-lg bg-slate-700/50 text-blue-300/60 hover:text-white hover:bg-slate-600/50 disabled:opacity-30 transition-all">
                        <ArrowDown className="w-4 h-4" />
                      </button>
                      <div className="flex-1" />
                      <button onClick={() => deleteBackground(bg.id)}
                        className="p-1.5 rounded-lg bg-red-500/10 text-red-400/60 hover:text-red-300 hover:bg-red-500/20 transition-all">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {saving[bg.id] && <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />}
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Info */}
            <div className="bg-slate-800/30 border border-blue-500/10 rounded-xl p-4 text-sm text-blue-300/50">
              {randomOrder ? (
                <p>Les fonds seront affichés dans un ordre aléatoire à chaque visite.</p>
              ) : (
                <p>Les fonds défilent automatiquement pour les utilisateurs sans abonnement Pro.</p>
              )}
              <p className="mt-1">Si un lien est défini, le visiteur peut cliquer sur le fond pour l'ouvrir.</p>
              <p className="mt-1">Formats acceptés : images (JPG, PNG, WebP) et vidéos (MP4, WebM) jusqu'à 200 Mo.</p>
            </div>
          </div>
        </div>
      </main>
      <DynamicFooter />
    </div>
  );
};

export default AdminBackgrounds;
